import { useState } from 'react'
import { Plus, Edit2, Trash2, X, AlertCircle, Clock, Users, CheckCircle } from 'lucide-react'

export default function IncidentResponsePlanPage() {
  const [incidents, setIncidents] = useState([
    {
      id: 1,
      name: 'Data Breach Response',
      severity: 'critical',
      status: 'active',
      responseTime: '15 minutes',
      escalationLevel: 3,
      teamLead: 'John Security',
      lastUpdated: new Date().toLocaleDateString()
    },
    {
      id: 2,
      name: 'System Outage Response',
      severity: 'high',
      status: 'active',
      responseTime: '30 minutes',
      escalationLevel: 2,
      teamLead: 'Sarah Infrastructure',
      lastUpdated: new Date().toLocaleDateString()
    }
  ])

  const [showModal, setShowModal] = useState(false)
  const [editingIncident, setEditingIncident] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    severity: 'high',
    responseTime: '30',
    escalationLevel: 2,
    teamLead: ''
  })

  const handleCreateClick = () => {
    setEditingIncident(null)
    setFormData({
      name: '',
      severity: 'high',
      responseTime: '30',
      escalationLevel: 2,
      teamLead: ''
    })
    setShowModal(true)
  }

  const handleEditIncident = (incident) => {
    setEditingIncident(incident)
    setFormData({
      name: incident.name,
      severity: incident.severity,
      responseTime: incident.responseTime.split(' ')[0],
      escalationLevel: incident.escalationLevel,
      teamLead: incident.teamLead
    })
    setShowModal(true)
  }

  const handleSaveIncident = () => {
    if (!formData.name || !formData.teamLead) {
      alert('Please fill in all required fields')
      return
    }

    if (editingIncident) {
      setIncidents(incidents.map(i =>
        i.id === editingIncident.id
          ? { ...i, ...formData, responseTime: formData.responseTime + ' minutes', lastUpdated: new Date().toLocaleDateString() }
          : i
      ))
    } else {
      const newIncident = {
        id: Date.now(),
        ...formData,
        responseTime: formData.responseTime + ' minutes',
        status: 'active',
        lastUpdated: new Date().toLocaleDateString()
      }
      setIncidents([...incidents, newIncident])
    }

    setShowModal(false)
  }

  const handleDeleteIncident = (id) => {
    if (confirm('Are you sure you want to delete this plan?')) {
      setIncidents(incidents.filter(i => i.id !== id))
    }
  }

  const getSeverityColor = (level) => {
    switch (level) {
      case 'critical': return 'bg-red-600/20 text-red-400'
      case 'high': return 'bg-orange-600/20 text-orange-400'
      case 'medium': return 'bg-yellow-600/20 text-yellow-400'
      case 'low': return 'bg-green-600/20 text-green-400'
      default: return 'bg-blue-600/20 text-blue-400'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <AlertCircle className="w-8 h-8 text-blue-400" />
            <h1 className="text-3xl font-bold text-white">Incident Response Plan</h1>
          </div>
          <p className="text-slate-400">Define response procedures, escalation paths, and team assignments for different incident types</p>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Response Plans</div>
            <div className="text-3xl font-bold text-blue-400">{incidents.length}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Critical Plans</div>
            <div className="text-3xl font-bold text-red-400">{incidents.filter(i => i.severity === 'critical').length}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Avg Response Time</div>
            <div className="text-3xl font-bold text-cyan-400">{Math.round(incidents.reduce((sum, i) => sum + parseInt(i.responseTime), 0) / incidents.length)} min</div>
          </div>
        </div>

        {/* Create Button */}
        <button
          onClick={handleCreateClick}
          className="mb-6 flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-semibold rounded-lg transition-all"
        >
          <Plus className="w-5 h-5" />
          Create Response Plan
        </button>

        {/* Plans List */}
        <div className="space-y-4">
          {incidents.map((incident) => (
            <div
              key={incident.id}
              className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-white">{incident.name}</h3>
                    <span className={`inline-flex items-center px-3 py-1 rounded text-xs font-bold ${getSeverityColor(incident.severity)}`}>
                      {incident.severity.toUpperCase()}
                    </span>
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                    <div>
                      <span className="text-slate-500 flex items-center gap-1"><Clock className="w-4 h-4" /> Response Time:</span>
                      <span className="text-white ml-2">{incident.responseTime}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 flex items-center gap-1"><AlertCircle className="w-4 h-4" /> Escalation Level:</span>
                      <span className="text-white ml-2">{incident.escalationLevel}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 flex items-center gap-1"><Users className="w-4 h-4" /> Team Lead:</span>
                      <span className="text-white ml-2">{incident.teamLead}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Last Updated:</span>
                      <span className="text-white ml-2">{incident.lastUpdated}</span>
                    </div>
                  </div>
                  <div className="bg-slate-700/30 rounded-lg p-3">
                    <p className="text-slate-400 text-xs">
                      This {incident.severity} incident type requires a {incident.responseTime} response time with {incident.escalationLevel} escalation level(s).
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEditIncident(incident)}
                    className="p-2 bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 rounded-lg transition-all"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDeleteIncident(incident.id)}
                    className="p-2 bg-red-600/20 text-red-400 hover:bg-red-600/30 rounded-lg transition-all"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg border border-blue-500/20 p-8 max-w-2xl w-full mx-4">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {editingIncident ? 'Edit Response Plan' : 'Create Response Plan'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-1 hover:bg-slate-700 rounded-lg transition-all"
                >
                  <X className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Incident Type Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
                    placeholder="e.g., Data Breach Response"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Team Lead *</label>
                  <input
                    type="text"
                    value={formData.teamLead}
                    onChange={(e) => setFormData({ ...formData, teamLead: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
                    placeholder="e.g., John Security"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Severity</label>
                    <select
                      value={formData.severity}
                      onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:outline-none"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Response Time (min)</label>
                    <input
                      type="number"
                      value={formData.responseTime}
                      onChange={(e) => setFormData({ ...formData, responseTime: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
                      min="1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Escalation Level</label>
                    <select
                      value={formData.escalationLevel}
                      onChange={(e) => setFormData({ ...formData, escalationLevel: parseInt(e.target.value) })}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:outline-none"
                    >
                      <option value="1">1 (Manager)</option>
                      <option value="2">2 (Director)</option>
                      <option value="3">3 (VP/CEO)</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-semibold transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveIncident}
                  className="px-6 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white rounded-lg font-semibold transition-all"
                >
                  Save Plan
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
